import { ChevronDown } from "@untitledui/icons";
import type { FC, ReactNode, Ref, RefAttributes } from "react";
import { isValidElement, useCallback } from "react";
import type { SelectProps as AriaSelectProps } from "react-aria-components";
import {
	Button as AriaButton,
	ListBox as AriaListBox,
	Select as AriaSelect,
	SelectValue as AriaSelectValue,
} from "react-aria-components";
import { Avatar } from "@/components/base/avatar/avatar";
import { HintText } from "@/components/base/input/hint-text";
import { Label } from "@/components/base/input/label";
import { cx } from "@/utils/cx";
import { isReactComponent } from "@/utils/is-react-component";
import { ComboBox } from "./combobox";
import { Popover } from "./popover";
import {
	type CommonProps,
	SelectContext,
	type SelectItemType,
	sizes,
} from "./select.types";
import { SelectItem } from "./select-item";

export type { SelectItemType };
export { sizes };

interface SelectProps
	extends Omit<AriaSelectProps<SelectItemType>, "children" | "items">,
		RefAttributes<HTMLDivElement>,
		CommonProps {
	items?: SelectItemType[];
	popoverClassName?: string;
	placeholderIcon?: FC | ReactNode;
	children: ReactNode | ((item: SelectItemType) => ReactNode);
	/** Whether to show supporting text in the selected value display. Defaults to true. */
	showSupportingTextInValue?: boolean;
	/** Load more items when user scrolls near bottom (e.g. for infinite list) */
	onLoadMore?: () => void;
	/** Whether there are more items to load */
	hasNextPage?: boolean;
	/** Whether the next page is currently loading */
	isFetchingNextPage?: boolean;
}

interface SelectValueProps {
	isOpen: boolean;
	size: "sm" | "md";
	isFocused: boolean;
	isDisabled: boolean;
	isInvalid?: boolean;
	placeholder?: string;
	ref?: Ref<HTMLButtonElement>;
	placeholderIcon?: FC | ReactNode;
	showSupportingTextInValue?: boolean;
}

const SelectValue = ({
	isOpen,
	isFocused,
	isDisabled,
	isInvalid,
	size,
	placeholder,
	placeholderIcon,
	showSupportingTextInValue = false,
	ref,
}: SelectValueProps) => {
	return (
		<AriaButton
			ref={ref}
			className={cx(
				"relative flex w-full cursor-pointer items-center rounded-lg bg-primary shadow-xs ring-1 ring-primary outline-hidden transition duration-100 ease-linear ring-inset",
				(isFocused || isOpen) && !isInvalid && "ring-2 ring-brand",
				isInvalid && "ring-error_subtle",
				isInvalid && (isFocused || isOpen) && "ring-2 ring-error",
				isDisabled && "cursor-not-allowed bg-disabled_subtle text-disabled",
			)}
		>
			<AriaSelectValue<SelectItemType>
				className={cx(
					"flex h-max w-full items-center justify-start gap-2 truncate text-left align-middle",

					// Icon styles
					"*:data-icon:size-5 *:data-icon:shrink-0 *:data-icon:text-fg-quaternary in-disabled:*:data-icon:text-fg-disabled",

					sizes[size].root,
				)}
			>
				{(state) => {
					const Icon = state.selectedItem?.icon || placeholderIcon;
					return (
						<>
							{state.selectedItem?.avatarUrl ? (
								<Avatar
									size="xs"
									src={state.selectedItem.avatarUrl}
									alt={state.selectedItem.label}
								/>
							) : isReactComponent(Icon) ? (
								<Icon data-icon aria-hidden="true" />
							) : isValidElement(Icon) ? (
								Icon
							) : null}

							{state.selectedItem ? (
								<section className="flex w-full gap-2 truncate">
									<p className="truncate text-md font-medium text-primary">
										{state.selectedItem?.label}
									</p>
									{showSupportingTextInValue &&
										state.selectedItem?.supportingText && (
											<p className="text-md text-tertiary">
												{state.selectedItem?.supportingText}
											</p>
										)}
								</section>
							) : (
								<p
									className={cx(
										"text-md text-placeholder",
										isDisabled && "text-disabled",
									)}
								>
									{placeholder}
								</p>
							)}

							<ChevronDown
								aria-hidden="true"
								className={cx(
									"ml-auto shrink-0 text-fg-quaternary",
									size === "sm" ? "size-4 stroke-[2.5px]" : "size-5",
								)}
							/>
						</>
					);
				}}
			</AriaSelectValue>
		</AriaButton>
	);
};

export { SelectContext };

const SCROLL_LOAD_MORE_THRESHOLD = 50;

const Select = ({
	placeholder = "Select",
	placeholderIcon,
	size = "sm",
	children,
	items,
	label,
	hint,
	tooltip,
	className,
	showSupportingTextInValue = true,
	onLoadMore,
	hasNextPage,
	isFetchingNextPage,
	...rest
}: SelectProps) => {
	const handlePopoverScroll = useCallback(
		(e: React.UIEvent<HTMLElement>) => {
			if (!onLoadMore || !hasNextPage || isFetchingNextPage) return;
			const el = e.currentTarget;
			if (
				el.scrollHeight - el.scrollTop - el.clientHeight <
				SCROLL_LOAD_MORE_THRESHOLD
			) {
				onLoadMore();
			}
		},
		[onLoadMore, hasNextPage, isFetchingNextPage],
	);

	return (
		<SelectContext.Provider value={{ size }}>
			<AriaSelect
				aria-label={!label ? placeholder : undefined}
				{...rest}
				className={(state) =>
					cx(
						"flex flex-col gap-1.5",
						typeof className === "function" ? className(state) : className,
					)
				}
			>
				{(state) => (
					<>
						{label && (
							<Label isRequired={state.isRequired} tooltip={tooltip}>
								{label}
							</Label>
						)}

						<SelectValue
							{...state}
							{...{ size, placeholder }}
							placeholderIcon={placeholderIcon}
							showSupportingTextInValue={showSupportingTextInValue}
						/>

						<Popover
							size={size}
							className={rest.popoverClassName}
							onScroll={onLoadMore ? handlePopoverScroll : undefined}
						>
							<AriaListBox items={items} className="size-full outline-hidden">
								{children}
							</AriaListBox>
						</Popover>

						{hint && <HintText isInvalid={state.isInvalid}>{hint}</HintText>}
					</>
				)}
			</AriaSelect>
		</SelectContext.Provider>
	);
};

const _Select = Select as typeof Select & {
	ComboBox: typeof ComboBox;
	Item: typeof SelectItem;
};
_Select.ComboBox = ComboBox;
_Select.Item = SelectItem;

export { _Select as Select };
